define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Enum/EnumOGCType',
    './OGCConnector',
    './WMTSInformation'
], function (
    defaultValue,
    defined,
    EnumOGCType,
    OGCConnector,
    WMTSInformation
) {
    'use strict'

    /**
     * WMTS服务连接器，用于获取WMTS服务元数据
     * @alias WMTSConnector
     * @constructor
     * @extends OGCConnector
     */
    class WMTSConnector extends OGCConnector {
		/**
         * @description 在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "WMTSConnector"
        /**
         * 创建WMTS服务连接器
         * @param {Object} props 配置选项
         * @param {String} props.url WMTS服务地址
         */
        constructor(props) {
            super({
                url: props.url,
                serviceType: EnumOGCType.WMTS,
                version: defaultValue(props.version, '1.0.0')
            });
        }

        /**
         * 获取WMTS服务能力信息
         * @param {Function} callback 回调函数，参数为(error, capabilities)
         */
        getCapabilities(callback) {
            const url = this._buildUrl({ REQUEST: 'GetCapabilities' });
            this._request(url, (error, response) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }

                try {
                    const xmlDoc = this._parseXML(response);
                    if (defined(callback)) {
                        callback(null, xmlDoc);
                    }
                } catch (e) {
                    if (defined(callback)) {
                        callback(new Error(`解析WMTS能力文档失败: ${e.message}`), null);
                    }
                }
            });
        }

        /**
         * 从能力文档中提取图层信息
         * @param {Document} capabilities XML能力文档
         * @returns {Array<WMTSInformation>} 图层信息数组
         * @protected
         * @override
         */
        _extractLayers(capabilities) {
            const layers = [];
            
            // WMTS可能使用命名空间
            const wmtsNS = 'http://www.opengis.net/wmts/1.0';
            const owsNS = 'http://www.opengis.net/ows/1.1';
            
            // 尝试使用命名空间获取Layer元素
            let layerNodes;
            try {
                // 先尝试使用命名空间
                layerNodes = capabilities.getElementsByTagNameNS(wmtsNS, 'Layer');
            } catch (e) {
                // 浏览器可能不支持命名空间API
                try {
                    // 尝试使用本地名称
                    layerNodes = capabilities.getElementsByTagName('Layer');
                } catch (err) {
                    console.error("获取图层节点失败:", err);
                    return layers;
                }
            }
            
            // 如果没有找到图层节点，尝试其他选择器
            if (!layerNodes || layerNodes.length === 0) {
                const contents = capabilities.getElementsByTagName('Contents')[0];
                if (contents) {
                    layerNodes = contents.getElementsByTagName('Layer');
                }
            }
            
            // 如果仍然没有找到，返回空数组
            if (!layerNodes || layerNodes.length === 0) {
                return layers;
            }
            
            // 提取TileMatrixSet信息，稍后会用到
            const tileMatrixSets = this._extractTileMatrixSets(capabilities);
            
            // 解析每个图层
            for (let i = 0; i < layerNodes.length; i++) {
                const layerNode = layerNodes[i];
                const layerInfo = this._parseLayerInfo(layerNode, tileMatrixSets);
                
                if (layerInfo) {
                    layers.push(layerInfo);
                }
            }
            
            return layers;
        }
        
        /**
         * 提取TileMatrixSet信息
         * @param {Document} capabilities XML能力文档
         * @returns {Object} TileMatrixSet信息映射表
         * @private
         */
        _extractTileMatrixSets(capabilities) {
            const tileMatrixSets = {};
            
            let tileMatrixSetNodes;
            try {
                const wmtsNS = 'http://www.opengis.net/wmts/1.0';
                tileMatrixSetNodes = capabilities.getElementsByTagNameNS(wmtsNS, 'TileMatrixSet');
            } catch (e) {
                tileMatrixSetNodes = capabilities.getElementsByTagName('TileMatrixSet');
            }
            
            if (!tileMatrixSetNodes || tileMatrixSetNodes.length === 0) {
                const contents = capabilities.getElementsByTagName('Contents')[0];
                if (contents) {
                    tileMatrixSetNodes = contents.getElementsByTagName('TileMatrixSet');
                }
            }
            
            if (tileMatrixSetNodes && tileMatrixSetNodes.length > 0) {
                for (let i = 0; i < tileMatrixSetNodes.length; i++) {
                    const tmsNode = tileMatrixSetNodes[i];
                    let identifier;
                    
                    try {
                        const owsNS = 'http://www.opengis.net/ows/1.1';
                        identifier = tmsNode.getElementsByTagNameNS(owsNS, 'Identifier')[0].textContent;
                    } catch (e) {
                        try {
                            identifier = tmsNode.getElementsByTagName('Identifier')[0].textContent;
                        } catch (err) {
                            continue;
                        }
                    }
                    
                    // 尝试获取支持的CRS
                    let supportedCRS = '';
                    try {
                        supportedCRS = tmsNode.getElementsByTagName('SupportedCRS')[0].textContent;
                    } catch (e) {
                        try {
                            supportedCRS = tmsNode.getElementsByTagName('ows:SupportedCRS')[0].textContent;
                        } catch (err) {
                            // 忽略错误
                        }
                    }
                    
                    tileMatrixSets[identifier] = {
                        identifier: identifier,
                        supportedCRS: supportedCRS,
                        // 可以根据需要添加更多信息
                    };
                }
            }
            
            return tileMatrixSets;
        }

        /**
         * 解析图层信息
         * @param {Element} layerNode 图层XML节点
         * @param {Object} tileMatrixSets TileMatrixSet信息映射表
         * @returns {WMTSInformation} WMTS图层信息对象
         * @private
         */
        _parseLayerInfo(layerNode, tileMatrixSets) {
            // 创建WMTSInformation对象
            const info = new WMTSInformation({
                version: this.version
            });
            
            // 获取基本信息
            const getElementContent = (node, tagName) => {
                try {
                    const elements = node.getElementsByTagName(tagName);
                    if (elements && elements.length > 0) {
                        return elements[0].textContent;
                    }
                } catch (e) { 
                    console.error(`获取${tagName}失败:`, e);
                }
                return null;
            };
            
            // 获取图层标识符（名称）
            info.layerName = getElementContent(layerNode, 'Identifier') || getElementContent(layerNode, 'ows:Identifier');
            
            // 如果没有找到图层名称，则该图层无效
            if (!info.layerName) {
                return null;
            }
            
            // 获取图层标题
            info.title = getElementContent(layerNode, 'Title') || getElementContent(layerNode, 'ows:Title') || info.layerName;
            
            // 获取图层描述
            info.abstract = getElementContent(layerNode, 'Abstract') || getElementContent(layerNode, 'ows:Abstract') || '';
            
            // 解析边界框
            this._parseBoundingBox(layerNode, info);
            
            // 解析可用样式
            this._parseStyles(layerNode, info);
            
            // 解析瓦片矩阵集链接
            this._parseTileMatrixSetLinks(layerNode, info, tileMatrixSets);
            
            // 解析图像格式
            this._parseFormats(layerNode, info);
            
            // 设置默认值
            info.minLevel = 0;
            info.maxLevel = 16; // 默认最大层级
            
            // 解析层级范围
            this._parseLevelRange(layerNode, info);
            
            // 设置自定义参数
            info.dpi = 90.71428571429; // 默认DPI
            info.customParams = ""
            
            return info;
        }
        
        /**
         * 解析图层边界框
         * @param {Element} layerNode 图层XML节点
         * @param {WMTSInformation} info 图层信息对象
         * @private
         */
        _parseBoundingBox(layerNode, info) {
            // 尝试获取WGS84边界框
            let wgs84BBox;
            try {
                wgs84BBox = layerNode.getElementsByTagName('WGS84BoundingBox')[0] || 
                            layerNode.getElementsByTagName('ows:WGS84BoundingBox')[0];
            } catch (e) {
                console.error("获取WGS84BoundingBox失败:", e);
            }
            
            if (wgs84BBox) {
                const getLowerCorner = () => {
                    try {
                        return wgs84BBox.getElementsByTagName('LowerCorner')[0]?.textContent || 
                               wgs84BBox.getElementsByTagName('ows:LowerCorner')[0]?.textContent;
                    } catch (e) {
                        return null;
                    }
                };
                
                const getUpperCorner = () => {
                    try {
                        return wgs84BBox.getElementsByTagName('UpperCorner')[0]?.textContent || 
                               wgs84BBox.getElementsByTagName('ows:UpperCorner')[0]?.textContent;
                    } catch (e) {
                        return null;
                    }
                };
                
                const lowerCorner = getLowerCorner();
                const upperCorner = getUpperCorner();
                
                if (lowerCorner && upperCorner) {
                    const lower = lowerCorner.split(' ');
                    const upper = upperCorner.split(' ');
                    
                    if (lower.length >= 2 && upper.length >= 2) {
                        info.minX = parseFloat(lower[0]);
                        info.minY = parseFloat(lower[1]);
                        info.maxX = parseFloat(upper[0]);
                        info.maxY = parseFloat(upper[1]);
                    }
                }
            }
            
            // 如果没有找到WGS84边界框，尝试其他边界框
            if (info.minX === 0 && info.maxX === 0 && info.minY === 0 && info.maxY === 0) {
                let boundingBox;
                try {
                    boundingBox = layerNode.getElementsByTagName('BoundingBox')[0] || 
                                 layerNode.getElementsByTagName('ows:BoundingBox')[0];
                } catch (e) {
                    console.error("获取BoundingBox失败:", e);
                }
                
                if (boundingBox) {
                    const crs = boundingBox.getAttribute('crs') || boundingBox.getAttribute('CRS');
                    if (crs) {
                        info.srs = crs; // 设置坐标参考系
                        
                        info.minX = parseFloat(boundingBox.getAttribute('minx') || boundingBox.getAttribute('lowercase'));
                        info.minY = parseFloat(boundingBox.getAttribute('miny') || boundingBox.getAttribute('mincorner'));
                        info.maxX = parseFloat(boundingBox.getAttribute('maxx') || boundingBox.getAttribute('uppercorner'));
                        info.maxY = parseFloat(boundingBox.getAttribute('maxy') || boundingBox.getAttribute('uppercorner'));
                    }
                }
            }
        }
        
        /**
         * 解析图层样式
         * @param {Element} layerNode 图层XML节点
         * @param {WMTSInformation} info 图层信息对象
         * @private
         */
        _parseStyles(layerNode, info) {
            let styleNodes;
            try {
                styleNodes = layerNode.getElementsByTagName('Style');
            } catch (e) {
                console.error("获取Style节点失败:", e);
                return;
            }
            
            if (styleNodes && styleNodes.length > 0) {
                for (let i = 0; i < styleNodes.length; i++) {
                    const style = styleNodes[i];
                    const isDefault = style.getAttribute('isDefault') === 'true';
                    
                    const styleName = (() => {
                        try {
                            return style.getElementsByTagName('Identifier')[0]?.textContent || 
                                   style.getElementsByTagName('ows:Identifier')[0]?.textContent || '';
                        } catch (e) {
                            return '';
                        }
                    })();
                    
                    const styleTitle = (() => {
                        try {
                            return style.getElementsByTagName('Title')[0]?.textContent || 
                                   style.getElementsByTagName('ows:Title')[0]?.textContent || styleName;
                        } catch (e) {
                            return styleName;
                        }
                    })();
                    
                    if (styleName) {
                        info.availableStyles.push({
                            name: styleName,
                            title: styleTitle,
                            isDefault: isDefault
                        });
                        
                        // 设置默认样式
                        if (isDefault) {
                            info.style = styleName;
                        }
                    }
                }
                
                // 如果没有标记为默认的样式，使用第一个样式
                if (!info.style && info.availableStyles.length > 0) {
                    info.style = info.availableStyles[0].name;
                }
            }
        }
        
        /**
         * 解析瓦片矩阵集链接
         * @param {Element} layerNode 图层XML节点
         * @param {WMTSInformation} info 图层信息对象
         * @param {Object} tileMatrixSets TileMatrixSet信息映射表
         * @private
         */
        _parseTileMatrixSetLinks(layerNode, info, tileMatrixSets) {
            let tileMatrixSetLinks;
            try {
                tileMatrixSetLinks = layerNode.getElementsByTagName('TileMatrixSetLink');
            } catch (e) {
                console.error("获取TileMatrixSetLink节点失败:", e);
                return;
            }
            
            if (tileMatrixSetLinks && tileMatrixSetLinks.length > 0) {
                for (let i = 0; i < tileMatrixSetLinks.length; i++) {
                    const link = tileMatrixSetLinks[i];
                    const tileMatrixSetId = (() => {
                        try {
                            return link.getElementsByTagName('TileMatrixSet')[0]?.textContent || '';
                        } catch (e) {
                            return '';
                        }
                    })();
                    
                    if (tileMatrixSetId) {
                        info.availableTileMatrixSets.push(tileMatrixSetId);
                        
                        // 如果在TileMatrixSets映射表中找到对应的CRS
                        if (tileMatrixSets[tileMatrixSetId] && tileMatrixSets[tileMatrixSetId].supportedCRS) {
                            info.availableCRS.push(tileMatrixSets[tileMatrixSetId].supportedCRS);
                        }
                    }
                }
                
                // 设置默认的瓦片矩阵集
                if (info.availableTileMatrixSets.length > 0) {
                    info.tileMatrixSet = info.availableTileMatrixSets[0];
                    
                    // 尝试设置默认的SRS
                    if (info.availableCRS.length > 0) {
                        info.srs = info.availableCRS[0];
                    } else if (tileMatrixSets[info.tileMatrixSet] && tileMatrixSets[info.tileMatrixSet].supportedCRS) {
                        info.srs = tileMatrixSets[info.tileMatrixSet].supportedCRS;
                    }
                }
            }
        }
        
        /**
         * 解析图像格式
         * @param {Element} layerNode 图层XML节点
         * @param {WMTSInformation} info 图层信息对象
         * @private
         */
        _parseFormats(layerNode, info) {
            let formatNodes;
            try {
                formatNodes = layerNode.getElementsByTagName('Format');
            } catch (e) {
                console.error("获取Format节点失败:", e);
                return;
            }
            
            if (formatNodes && formatNodes.length > 0) {
                // 设置第一个格式为默认格式
                info.format = formatNodes[0].textContent || 'image/png';
                
                // 偏好PNG格式
                for (let i = 0; i < formatNodes.length; i++) {
                    const format = formatNodes[i].textContent;
                    if (format && format.toLowerCase().includes('png')) {
                        info.format = format;
                        break;
                    }
                }
            }
        }
        
        /**
         * 解析层级范围
         * @param {Element} layerNode 图层XML节点
         * @param {WMTSInformation} info 图层信息对象
         * @private
         */
        _parseLevelRange(layerNode, info) {
            // 在部分WMTS实现中，可能有层级范围信息
            // 这个信息可能存在于TileMatrixSetLink的TileMatrixSetLimits中
            let tileMatrixSetLinks;
            try {
                tileMatrixSetLinks = layerNode.getElementsByTagName('TileMatrixSetLink');
            } catch (e) {
                return;
            }
            
            if (tileMatrixSetLinks && tileMatrixSetLinks.length > 0) {
                for (let i = 0; i < tileMatrixSetLinks.length; i++) {
                    const link = tileMatrixSetLinks[i];
                    const tileMatrixSetId = (() => {
                        try {
                            return link.getElementsByTagName('TileMatrixSet')[0]?.textContent || '';
                        } catch (e) {
                            return '';
                        }
                    })();
                    
                    // 只处理当前选择的瓦片矩阵集
                    if (tileMatrixSetId === info.tileMatrixSet) {
                        let tileMatrixLimits;
                        try {
                            tileMatrixLimits = link.getElementsByTagName('TileMatrixSetLimits');
                        } catch (e) {
                            continue;
                        }
                        
                        if (tileMatrixLimits && tileMatrixLimits.length > 0) {
                            const limits = tileMatrixLimits[0].getElementsByTagName('TileMatrixLimits');
                            if (limits && limits.length > 0) {
                                // 设置最小和最大层级
                                info.minLevel = 0
                                info.maxLevel = limits.length - 1
                            }
                        }
                    }
                }
            }
        }

        /**
         * 获取指定图层的WMTSInformation对象
         * @param {String} layerName 图层名称
         * @param {Function} callback 回调函数，参数为(error, wmtsInfo)
         */
        getLayerInformation(layerName, callback) {
            this.getLayerInfo(layerName, (error, layerInfo) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }

                if (defined(callback)) {
                    callback(null, layerInfo);
                }
            });
        }
    }

    return WMTSConnector;
});